export const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export interface HourWindow {
    startHour: number;
    endHour: number;
}

export const DEFAULT_HOUR_WINDOW: HourWindow = { startHour: 8, endHour: 18 };
export const SLOTS_PER_HOUR = 2;
export const SUBROWS_PER_SLOT = 3;
export const SUBROWS_PER_HOUR = SLOTS_PER_HOUR * SUBROWS_PER_SLOT;
export const SLOT_MINUTES = 60 / SLOTS_PER_HOUR;
export const SUBROW_MINUTES = 60 / SUBROWS_PER_HOUR;
export const HEADER_ROWS = 1;

export const gridRowCount = (window: HourWindow): number =>
    HEADER_ROWS + (window.endHour - window.startHour) * SUBROWS_PER_HOUR;

const hourLabel = (hour: number): string => {
    const hour12 = hour % 12 || 12;
    return `${hour12}${hour >= 12 ? "PM" : "AM"}`;
};

export const hourLabels = (window: HourWindow): string[] => {
    const labels: string[] = [];
    for (let hour = window.startHour; hour < window.endHour; hour++) {
        labels.push(hourLabel(hour));
    }
    return labels;
};

export interface HourWindowItem {
    timeStr: string | null;
    durationMinutes: number;
}

export const computeHourWindow = (items: HourWindowItem[]): HourWindow => {
    let { startHour, endHour } = DEFAULT_HOUR_WINDOW;
    for (const item of items) {
        if (!item.timeStr) {
            continue;
        }
        const [hours, minutes] = item.timeStr.split(":").map(Number);
        if (isNaN(hours)) {
            continue;
        }
        startHour = Math.min(startHour, hours);
        const itemEndMinutes =
            hours * 60 + (minutes || 0) + Math.max(item.durationMinutes, 0);
        endHour = Math.max(endHour, hours + 1, Math.ceil(itemEndMinutes / 60));
    }
    return { startHour: Math.max(0, startHour), endHour: Math.min(24, endHour) };
};

export const MONTH_ABBREVIATIONS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

export const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

export const getGridDates = (
    year: number,
    month: number,
): { date: Date; isCurrentMonth: boolean }[] => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay();
    const gridStart = new Date(year, month, 1 - startOffset);

    const totalDays = startOffset + lastDay.getDate();
    const rows = Math.ceil(totalDays / 7);
    const cellCount = rows * 7;

    const dates: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = 0; i < cellCount; i++) {
        const d = new Date(gridStart);
        d.setDate(gridStart.getDate() + i);
        dates.push({
            date: d,
            isCurrentMonth: d.getMonth() === month,
        });
    }
    return dates;
};

export const formatDateKey = (d: Date): string => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

export const formatTime = (timeStr: string | null | undefined): string => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
};

export const getWeekDays = (date: Date): Date[] => {
    const result: Date[] = [];
    const day = date.getDay();
    const diff = date.getDate() - day;

    for (let i = 0; i < 7; i++) {
        const d = new Date(date);
        d.setDate(diff + i);
        result.push(d);
    }
    return result;
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
};

export const parseTimeToRow = (
    timeStr: string | null | undefined,
    window: HourWindow,
): number | null => {
    if (!timeStr) return null;

    const [hours, minutes] = timeStr.split(":").map(Number);

    if (hours < window.startHour || hours >= window.endHour) return null;

    const hourOffset = (hours - window.startHour) * SUBROWS_PER_HOUR;
    const slotOffset = Math.floor(minutes / SLOT_MINUTES) * SUBROWS_PER_SLOT;
    return hourOffset + slotOffset + HEADER_ROWS + 1;
};

export interface EventLayout {
    row: number;
    span: number;
    columnIndex: number;
    totalColumns: number;
}

interface PlacedEvent<T> {
    event: T;
    row: number;
    span: number;
    columnIndex: number;
}

interface Cluster<T> {
    placed: PlacedEvent<T>[];
    totalColumns: number;
}

export const tourDurationToSpan = (
    minutes: number,
    startRow: number,
    window: HourWindow,
): number => {
    const naive = Math.max(1, Math.ceil(minutes / SUBROW_MINUTES));
    const remaining = gridRowCount(window) - startRow + 1;
    return Math.min(naive, Math.max(1, remaining));
};

export const computeOverlapLayout = <T>(
    events: { event: T; row: number; span: number }[],
): { event: T; layout: EventLayout }[] => {
    if (events.length === 0) return [];

    const sorted = events.slice().sort((a, b) => {
        if (a.row !== b.row) return a.row - b.row;
        return b.span - a.span;
    });

    const clusters: Cluster<T>[] = [];
    let current: Cluster<T> = { placed: [], totalColumns: 0 };
    let columnEndRows: number[] = [];
    let clusterEnd = -Infinity;

    for (const evt of sorted) {
        if (current.placed.length > 0 && evt.row >= clusterEnd) {
            clusters.push(current);
            current = { placed: [], totalColumns: 0 };
            columnEndRows = [];
            clusterEnd = -Infinity;
        }

        let assigned = columnEndRows.findIndex(end => end <= evt.row);
        if (assigned === -1) {
            assigned = columnEndRows.length;
            columnEndRows.push(0);
        }
        columnEndRows[assigned] = evt.row + evt.span;

        current.placed.push({
            event: evt.event,
            row: evt.row,
            span: evt.span,
            columnIndex: assigned,
        });
        current.totalColumns = columnEndRows.length;
        clusterEnd = Math.max(clusterEnd, evt.row + evt.span);
    }
    if (current.placed.length > 0) clusters.push(current);

    const results: { event: T; layout: EventLayout }[] = [];
    for (const cluster of clusters) {
        for (const placed of cluster.placed) {
            results.push({
                event: placed.event,
                layout: {
                    row: placed.row,
                    span: placed.span,
                    columnIndex: placed.columnIndex,
                    totalColumns: cluster.totalColumns,
                },
            });
        }
    }
    return results;
};

export const parseDateTimeToTimeStr = (
    isoString: string | null | undefined,
): string | null => {
    if (!isoString) return null;
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return null;
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
};

export const parseDateTimeToDateKey = (
    isoString: string | null | undefined,
): string | null => {
    if (!isoString) return null;
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return null;
    return formatDateKey(d);
};

export const formatSidebarDate = (dateValue: string): string => {
    const [year, month, day] = dateValue.split("-").map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

export const addDaysToDate = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export const localDateKey = (isoInstant: string, timeZone: string): string => {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(new Date(isoInstant));
    const value = (type: string) => parts.find(part => part.type === type)?.value ?? "";
    return `${value("year")}-${value("month")}-${value("day")}`;
};

export const browserTimeZone = (): string =>
    Intl.DateTimeFormat().resolvedOptions().timeZone;
